import { pushDL } from "@/lib/analytics";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import apiClient from "@/api/apiClient";
import { getSubscriptionPlans, createCheckoutSession } from "@/api/subscription";
import PlanActionResultDialog from "@/components/models/PlanActionResultDialog";

type SubscriptionRow = {
  id: string;
  plan_name: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string | null;
};

type PaymentRow = {
  id: string;
  amount: number | null;
  currency: string | null;
  status: string | null;
  created_at: string | null;
};

const durationMap: Record<string, string> = {
  Light: "5 days",
  Pro: "15 days",
  Mastery: "30 days",
};

const planRank: Record<string, number> = {
  light: 1,
  pro: 2,
  mastery: 3,
};

const Subscription = () => {
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState<SubscriptionRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [availablePlans, setAvailablePlans] = useState<
    Array<{ id: string; name: string; formatted_amount: string; unit_amount: number }>
  >([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);

  const [resultOpen, setResultOpen] = useState(false);
  const [resultTitle, setResultTitle] = useState("");
  const [resultMessage, setResultMessage] = useState("");

  // scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // fetch subs + payments + plans
  useEffect(() => {
    setLoading(true);

    apiClient
      .post("/get_user_subscriptions", {})
      .then((res) => {
        setSubs(res.data.subscriptions || []);
        setPayments(res.data.payments || []);
      })
      .finally(() => setLoading(false));

    (async () => {
      try {
        const plansData = await getSubscriptionPlans();
        const prices: Array<{ id: string; name: string; formatted_amount: string; unit_amount: number }> = [];

        plansData.products?.forEach((p: any) => {
          (p.prices || []).forEach((pr: any) => {
            prices.push({
              id: pr.id,
              name: p.name,
              formatted_amount: pr.formatted_amount,
              unit_amount: pr.unit_amount,
            });
          });
        });

        // Sort by price ascending
        prices.sort((a, b) => a.unit_amount - b.unit_amount);

        setAvailablePlans(prices);
      } catch (err) {
        console.warn("Failed to load plans:", err);
      } finally {
        setPlansLoading(false);
      }
    })();
  }, []);

  const formatStatus = (s: string | null) => {
    if (!s) return "-";
    if (s.toLowerCase() === "cancel_scheduled") return "Active";
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const activeSub =
    subs.find(
      (s) =>
        s.status &&
        (s.status.toLowerCase() === "active" ||
          s.status.toLowerCase() === "cancel_scheduled")
    ) || null;

  const currentPlanName = activeSub?.plan_name || null;
  const currentRank = currentPlanName
    ? planRank[currentPlanName.toLowerCase()] || 0
    : 0;

    // Prefer active subscription, otherwise fall back to most recent one (expired)
  const displaySub =
    activeSub ??
    (subs.length > 0
      ? [...subs].sort(
          (a, b) =>
            new Date(b.end_date || 0).getTime() -
            new Date(a.end_date || 0).getTime()
        )[0]
      : null);
  


  return (
    <div className="w-full self-start flex-none flex flex-col pt-16 pb-16 md:pt-24 md:pb-24 bg-white">
      {/* 16px mobile / 24px tablet+ / 24px desktop side padding */}
      <div className="w-full max-w-[1216px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8 lg:mb-12">
          <h1 className="text-[24px] md:text-[36px] font-bold tracking-[-0.5px] text-[#1D232A] leading-[1.3]">
            Manage your subscription
          </h1>
          <p className="text-[16px] text-[#667085] mt-3 leading-[1.5]">
            Renew or upgrade your plan to continue accessing exam practice and feedback.
          </p>
        </div>

        {/* Active subscription */}
        <div className="bg-white border border-[#0000000F] rounded-[16px] p-4 md:p-6 lg:p-8 mb-6 md:mb-8 lg:mb-16">
          <h2 className="text-[18px] md:text-[20px] font-semibold text-[#1D232A] mb-4">
            Active subscription
          </h2>

          {subs.length === 0 ? (
            payments.length > 0 ? (
              <p className="text-[16px] font-medium text-[#EF4444]">
                Your plan has expired.
              </p>
            ) : (
              <p className="text-[16px] text-[#667085]">
                You currently have no active subscription.
              </p>
            )
          ) : (
            <div className="flex flex-col gap-4 text-[15px]">
              <div className="flex items-center justify-between">
                <span className="text-[14px] text-[#667085]">Plan</span>
                <span className="text-[15px] font-medium text-[#1D232A]">
                  {displaySub?.plan_name || "—"}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-[14px] text-[#667085]">Duration</span>
                <span className="text-[15px] text-[#1D232A]">
                  {durationMap[displaySub?.plan_name || ""] || "—"}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-[14px] text-[#667085]">Start date</span>
                <span className="text-[15px] text-[#1D232A]">
                  {displaySub?.start_date
                    ? new Date(displaySub.start_date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "—"}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-[14px] text-[#667085]">End date</span>
                <span className="text-[15px] text-[#1D232A]">
                  {displaySub?.end_date
                    ? new Date(displaySub.end_date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                    : "—"}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-[14px] text-[#667085]">Status</span>
                <span
                  className={`px-3 py-1 rounded-full text-white text-[13px] ${
                    displaySub?.status?.toLowerCase() === "active"
                      ? "bg-[#21C45D]"
                      : "bg-[#EF4444]"
                  }`}
                >
                  {formatStatus(displaySub?.status)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Plans */}
        <h2 className="text-[18px] md:text-[24px] font-semibold text-[#1D232A] mb-6 md:mb-8">
          Available plans
        </h2>

        <p className="text-[14px] text-[#667085] -mt-3 mb-6 md:mb-8 leading-[1.5]">
          Plans reset on upgrade or renewal. Unused days do not roll over.
          </p>


        {plansLoading ? (
          <div className="w-full flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[...availablePlans]
              .sort((a, b) => {
                const ra = planRank[a.name.toLowerCase()] ?? 999;
                const rb = planRank[b.name.toLowerCase()] ?? 999;
                return ra - rb; // Light -> Pro -> Mastery
              })
              .map((plan) => {
                const rank = planRank[plan.name.toLowerCase()] || 0;
                const canUpgrade = rank > currentRank;

                return (
                  <div
                    key={plan.id}
                    className="p-4 md:p-6 rounded-[16px] border border-[#0000000F] bg-white transition-all hover:scale-[1.01] hover:shadow-[0px_8px_20px_rgba(0,0,0,0.08)]"
                  >
                    <h3 className="text-[20px] font-semibold text-[#1D232A] mb-1">
                      {plan.name}
                    </h3>
                    <p className="text-[14px] text-[#667085] mb-3">
                      {durationMap[plan.name] || "—"}
                    </p>

                    <p className="text-[18px] font-semibold text-[#1D232A] mb-6">
                      {plan.formatted_amount}
                    </p>

                    <Button
                      className={`w-full h-[48px] rounded-[10px] text-[14px] font-medium ${
                        canUpgrade
                          ? "bg-[#0058CC] text-white hover:bg-[#004CAD]"
                          : "bg-[#E5E7EB] text-[#6B7280] cursor-not-allowed"
                      } ${loadingPriceId === plan.id ? "opacity-70 cursor-not-allowed" : ""}`}
                      disabled={!canUpgrade || !!loadingPriceId}
                      onClick={async () => {
                        if (!canUpgrade || loadingPriceId) return;
                        try {
                          setLoadingPriceId(plan.id);
                          const successUrl = `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`;
                          const cancelUrl = `${window.location.origin}/payment-canceled`;
                          pushDL("begin_checkout", {
                            plan_name: plan.name,
                            price_id: plan.id,
                            source: "manage_subscription",
                          });
                          const resp = await createCheckoutSession(
                            plan.id,
                            successUrl,
                            cancelUrl
                          );
                          if (resp?.checkout_url) {
                            window.location.href = resp.checkout_url;
                            // Keep loading state while redirecting
                            setTimeout(() => setLoadingPriceId(null), 5000);
                          } else {
                             setLoadingPriceId(null);
                          }
                        } catch (err) {
                          console.error("Checkout error:", err);
                          setLoadingPriceId(null);
                        }
                      }}
                    >
                      {loadingPriceId === plan.id ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Processing...
                        </span>
                      ) : (
                          canUpgrade
                            ? "Upgrade plan"
                            : rank === currentRank
                            ? "Current plan"
                            : "Unavailable"
                        )}
                    </Button>
                  </div>
                );
              })}
          </div>
        )}

        <p className="text-[13px] text-[#98A2B3] mt-6 text-center">
          Purchases for Light, Pro, and Mastery plans are non-refundable.
        </p>

        <PlanActionResultDialog
          open={resultOpen}
          onOpenChange={(open) => {
            setResultOpen(open);
            if (!open) window.location.reload();
          }}
          title={resultTitle}
          message={resultMessage}
        />
      </div>
    </div>
  );
};

export default Subscription;
